import { useNavigate, useSubmit } from '@remix-run/react';
import { ComponentPropsWithRef, useCallback, useState } from 'react';
import { ValidatedForm } from 'remix-validated-form';
import { DevErrorLog, GlobalError } from '../FormFields';
import { gaEvent } from '../GoogleAnalytics';
import { useGoogleRecaptcha } from './useGoogleRecaptcha';

interface HoneypotFormProps<T> extends Omit<ComponentPropsWithRef<typeof ValidatedForm>, 'method'> {
	honeyField?: string;
	recaptchaKey?: string;
	confirm?: string;
	recaptchaAction?: string;
	gaCategory?: string;
	gaLabelKey?: keyof T;
}

export function HoneypotForm<T>({
	children,
	honeyField,
	recaptchaKey = '',
	recaptchaAction,
	confirm,
	gaCategory,
	gaLabelKey,
	fetcher,
	onSubmit,
	...formProps
}: HoneypotFormProps<T>) {
	const [gotcha, setGotcha] = useState('');
	const [recaptchaToken, setToken] = useState('');
	const { ready, execute } = useGoogleRecaptcha({ recaptchaKey, action: recaptchaAction });
	let submit = useSubmit();
	const navigate = useNavigate();

	const handleSubmit = useCallback(
		async (data: any, e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			const target = e.target as HTMLFormElement;

			if (!!confirm && !window.confirm(confirm)) return;

			if (gaCategory) {
				gaEvent({
					action: 'submit_form',
					category: gaCategory,
					label: gaLabelKey ? JSON.stringify(data[gaLabelKey] || '') : 'General',
				});
			}

			if (gotcha) {
				console.log('Honeypot caught a bot with value:', gotcha);
				navigate('/');
				return;
			}

			if (!!recaptchaKey) {
				if (ready) {
					const newToken = await execute();
					setToken(newToken);
				} else {
					console.log('Recaptcha not ready');
				}
			}

			//Delay the submit to allow recaptcha to finish
			setTimeout(() => {
				if (onSubmit) {
					onSubmit(data, e);
				} else if (fetcher) {
					fetcher.submit(target);
				} else {
					submit(target);
				}
			}, 0);
		},
		[gotcha, ready, fetcher, confirm, execute, submit],
	);

	return (
		<ValidatedForm method="post" fetcher={fetcher} onSubmit={handleSubmit} {...formProps}>
			{children}
			{honeyField && (
				<input
					className="absolute left-[-5000px]"
					autoComplete="new-password"
					type="text"
					aria-autocomplete="none"
					name={honeyField}
					tabIndex={-1}
					value={gotcha}
					onChange={e => setGotcha(e.target.value)}
				/>
			)}
			<input type="hidden" name="recaptchaToken" value={recaptchaToken} />
			<input type="hidden" name="recaptchaAction" value={recaptchaAction} />
			<DevErrorLog />
			<GlobalError />
		</ValidatedForm>
	);
}
